import React from 'react';
import { Rnd } from 'react-rnd';
import { DEFAULT_DIMENSIONS } from './annotation';
import { Annotation } from './annotation';
import styles from './document-annotation.module.css';
import { getDocumentAnnotationColor } from '../../util/get-document-anntation-color';
import { getAnnotationIcon } from '../../util/get-annotation-icon';
import { X } from 'lucide-react';
import type { Size } from '@/components/pdf-viewer';
import { annotationInPageBasePx } from './annotation-geometry';
import { getClientDeviceType } from '@/utils/get-client-device-type';

/**
 * Smallest touch hit area, in screen px, for the remove button and the resize
 * handle. The visible glyphs stay small; only the area that takes the tap grows.
 */
export const MIN_TOUCH_TARGET_PX = 32;
/** react-rnd's own handle size (base px), kept on desktop. */
const DEFAULT_HANDLE_PX = 20;

export type DocumentAnnotationProps = {
	annotation: Annotation;
	/** Base size (96 dpi px) of the page overlay the field lives on. */
	pageSize: Size;
	/** Current viewer zoom; react-rnd needs it to map pointer deltas to base px. */
	zoom: number;
	color?: string;
	isResizable?: boolean;
	onUpdate: (id: string, updatedAnnotation: Annotation) => void;
	onRemove: (id: Annotation['id']) => void;
};

export const DocumentAnnotation = (props: DocumentAnnotationProps) => {
	// Normalised to this page's base px so react-rnd and the stored values agree,
	// whatever containerRect the annotation was created with.
	const annotation = annotationInPageBasePx(props.annotation, props.pageSize);
	const annotationColors = getDocumentAnnotationColor(annotation.type);
	// The overlay is scaled by zoom, so a fixed screen size is target / zoom base px.
	const touchTarget =
		getClientDeviceType() === 'desktop'
			? undefined
			: Math.max(DEFAULT_HANDLE_PX, MIN_TOUCH_TARGET_PX / props.zoom);
	return (
		<Rnd
			scale={props.zoom}
			// Without this react-draggable cancels the touchstart on the button,
			// so a tap never becomes a click.
			cancel={`.${styles['remove-annotation']}`}
			resizeHandleStyles={
				touchTarget
					? {
							bottomRight: {
								width: touchTarget,
								height: touchTarget,
								right: -touchTarget / 2,
								bottom: -touchTarget / 2,
							},
						}
					: undefined
			}
			size={{
				width: annotation.width,
				height: annotation.height,
			}}
			enableResizing={{
				top: false,
				right: false,
				bottom: false,
				left: false,
				topRight: false,
				bottomRight: props.isResizable,
				bottomLeft: false,
				topLeft: false,
			}}
			position={{ x: annotation.x, y: annotation.y }}
			onDragStop={(_event, data) => {
				props.onUpdate(annotation.id, {
					...annotation,
					x: data.x,
					y: data.y,
				});
			}}
			onResizeStop={(_event, _direction, ref, _delta, position) => {
				// ref.style is in the overlay's own (unscaled) px, i.e. base px.
				props.onUpdate(annotation.id, {
					...annotation,
					x: position.x,
					y: position.y,
					width:
						parseFloat(ref.style.width) || DEFAULT_DIMENSIONS.WIDTH,
					height:
						parseFloat(ref.style.height) ||
						DEFAULT_DIMENSIONS.HEIGHT,
				});
			}}
			bounds="parent"
			style={{
				cursor: 'move',
			}}
		>
			<button
				type="button"
				aria-label="Quitar campo"
				className={styles['remove-annotation']}
				style={
					touchTarget
						? ({
								'--touch-target': `${touchTarget}px`,
							} as React.CSSProperties)
						: undefined
				}
				onClick={(event) => {
					event.stopPropagation();
					event.preventDefault();
					props.onRemove(annotation.id);
				}}
			>
				<X size={8} />
			</button>
			<div
				data-annotation-id={annotation.id}
				className={styles['document-annotation']}
				style={{
					borderColor: props.color ?? annotationColors.border,
					backgroundColor: props.color
						? `${props.color}50`
						: annotationColors.background,
				}}
			>
				{['date', 'text'].includes(annotation.type) ? (
					<input
						type={annotation.type}
						value={annotation.value}
						placeholder="Escribe tu texto aquí..."
						onChange={(event) =>
							props.onUpdate(annotation.id, {
								...annotation,
								value: event.target.value,
							})
						}
					/>
				) : (
					<></>
				)}
				{getAnnotationIcon(
					annotation.type,
					props.color ?? annotationColors.border,
				)}
				{props.isResizable && (
					<div
						id="document-annotation-resizer"
						className={styles['resizer']}
					/>
				)}
			</div>
		</Rnd>
	);
};
